// components/Security.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function Security() {
    return (
        <section className="py-20 md:py-28 bg-gray-50">
            <Helmet>
                <title>Security | Arxis Technologies Limited</title>
                <meta name="description" content="Your financial data is the heart of your business. We treat it with the highest level of care and protection." />
            </Helmet>

            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                {/* Hero/Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Security at Arxis Accounting
                    </h1>
                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                        Your financial data is the heart of your business. We treat it with the highest level of care and protection.
                        Arxis Accounting is built on enterprise-grade security practices to keep your information safe, compliant, and private — so you can focus on growth with total peace of mind.
                    </p>
                </div>

                {/* Security Highlights / Icons Grid */}
                <div className="grid md:grid-cols-3 gap-8 mb-20">
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
                        <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">🔒</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Bank-Grade Encryption</h3>
                        <p className="text-gray-700">
                            All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Sensitive fields like financial records and credentials are protected end-to-end.
                        </p>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
                        <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">🛡️</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Access Controls & Authentication</h3>
                        <p className="text-gray-700">
                            Role-based access, multi-factor authentication (MFA), session timeouts, IP restrictions, and audit logs for every action in your account.
                        </p>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
                        <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">📜</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Regulatory Compliance</h3>
                        <p className="text-gray-700">
                            Fully compliant with Nigeria Data Protection Act 2023 (NDPA), NDPR, and relevant CBN guidelines for fintech data handling. Regular independent audits.
                        </p>
                    </div>
                </div>

                {/* Detailed Security Practices */}
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16 border border-gray-100">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        How We Protect Your Data
                    </h2>

                    <div className="space-y-10">
                        <div>
                            <h3 className="text-2xl font-semibold text-[#4285F4] mb-4">1. Data Encryption Everywhere</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Every piece of data — from login credentials to invoices and payroll records — is encrypted using industry-standard protocols.
                                We use AES-256 for data at rest in our secure cloud databases and TLS 1.3 for all communications between your device and our servers.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-2xl font-semibold text-[#4285F4] mb-4">2. Secure Infrastructure & Hosting</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Hosted on trusted cloud providers with ISO 27001-certified data centers. We implement firewalls, DDoS protection, intrusion detection,
                                and regular vulnerability scanning. Access to production systems is strictly limited and monitored.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-2xl font-semibold text-[#4285F4] mb-4">3. Authentication & Authorization</h3>
                            <p className="text-gray-700 leading-relaxed">
                                Enforce strong passwords, mandatory MFA for all accounts (especially admins), automatic session expiration,
                                and granular permissions so users only see what they need. We log and alert on suspicious login attempts.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-2xl font-semibold text-[#4285F4] mb-4">4. Compliance & Auditing</h3>
                            <p className="text-gray-700 leading-relaxed">
                                We align with NDPA/NDPR requirements, including lawful processing, data minimization, and breach notification within 72 hours.
                                Annual penetration testing, security audits, and compliance reviews are conducted by independent third parties.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-2xl font-semibold text-[#4285F4] mb-4">5. Incident Response & Business Continuity</h3>
                            <p className="text-gray-700 leading-relaxed">
                                We maintain a formal incident response plan, regular backups (encrypted and geographically redundant),
                                and disaster recovery procedures to ensure availability even in worst-case scenarios.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-2xl font-semibold text-[#4285F4] mb-4">6. Third-Party Vendors & Integrations</h3>
                            <p className="text-gray-700 leading-relaxed">
                                All partners and processors (e.g., payment gateways, cloud providers) are vetted for security and sign strict data processing agreements.
                                We limit data shared to the minimum necessary.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Trust Signals / Certifications (placeholders) */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">
                        Built on Trust & Transparency
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                        We undergo regular security assessments and comply with global best practices while meeting Nigerian regulatory standards.
                        Your trust is our top priority.
                    </p>

                    {/* You can add badge-like icons here later */}
                    <div className="flex flex-wrap justify-center gap-6">
                        <div className="bg-white px-6 py-4 rounded-lg shadow border border-gray-200">
                            NDPA Compliant
                        </div>
                        <div className="bg-white px-6 py-4 rounded-lg shadow border border-gray-200">
                            AES-256 Encryption
                        </div>
                        <div className="bg-white px-6 py-4 rounded-lg shadow border border-gray-200">
                            Multi-Factor Auth
                        </div>
                        <div className="bg-white px-6 py-4 rounded-lg shadow border border-gray-200">
                            Regular Audits
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">
                        Have Security Questions?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Our team is happy to discuss our practices in detail or provide more information on compliance.
                    </p>
                    <a
                        href="/contact"
                        className="inline-block px-10 py-5 bg-[#4285F4] text-white font-semibold text-lg rounded-xl hover:bg-[#336bd1] transition shadow-lg"
                    >
                        Contact Us About Security
                    </a>
                </div>
            </div>
        </section>
    );
}